import React from 'react';
import { Navbar, MathBlock } from '../../components';
import ArticleLayout from '../../components/ArticleLayout/ArticleLayout';
import contextEngineeringGraph from '../../assets/foundations/context-engineering-graph.svg';

const ContextEngineering = () => (
  <div>
    <Navbar />
    <ArticleLayout
      title="Context Engineering for Agentic Systems"
      subtitle="Context construction — not task decomposition — is the primary optimization target for scalable multi-agent systems."
      author="Rajeev Bakshi"
      readTime="24 min read"
    >
      <h2>The Problem with Current Multi-Agent Architectures</h2>
      <p>
        Modern AI systems increasingly deploy collections of specialized agents to solve
        complex tasks. A code review system might launch independent agents for correctness,
        security, performance, and maintainability. An enterprise knowledge assistant might
        route queries to domain-specific reasoners. A scientific discovery platform might
        orchestrate literature retrieval, hypothesis generation, and experimental design.
      </p>
      <p>
        These architectures share a common computational pattern: an event triggers reasoning,
        a planner determines which agents participate, each agent retrieves context, each agent
        reasons independently, and outputs are aggregated into a final result.
      </p>
      <p>
        The prevailing optimization target is <em>task decomposition</em> — how to divide
        reasoning across agents to maximize specialization and quality. Substantial research
        has focused on agent roles, communication protocols, and orchestration strategies.
      </p>
      <p>
        Comparatively little attention has been paid to the computational properties of
        <strong> context itself</strong>.
      </p>
      <p>
        This creates a fundamental inefficiency. Multiple agents operating on the same task
        frequently retrieve and process nearly identical information before performing
        different analyses. The dominant computational cost is not reasoning — it is the
        repeated construction of overlapping context.
      </p>

      <h2>Context as a Computational Resource</h2>
      <p>
        We propose that context should be elevated to a first-class systems resource —
        constructed, optimized, cached, and distributed with the same rigor that classical
        systems apply to memory, network bandwidth, and compute cycles.
      </p>
      <p>
        The analogy to operating systems is instructive. Early computers optimized instruction
        execution. Later systems recognized that <em>memory locality</em> often dominates
        overall performance. Cache hierarchies, virtual memory, and memory-mapped I/O emerged
        as the discipline matured.
      </p>
      <p>
        We hypothesize that agentic AI systems are at an analogous inflection point. Future
        systems will derive greater benefit from optimizing context than from increasing the
        sophistication of reasoning alone.
      </p>
      <p>
        This hypothesis motivates a new systems discipline: <strong>Context Engineering</strong>.
      </p>

      <div className="article-callout">
        <p>
          <strong>Central thesis:</strong> Reasoning quality depends more strongly on context
          quality than on context quantity. The goal is not to maximize information retrieval
          but to construct the minimum context sufficient to preserve reasoning quality.
        </p>
      </div>

      <h2>Three Forms of Context</h2>
      <p>
        A common source of confusion in discussions about context engineering is that the
        word &ldquo;context&rdquo; conflates three fundamentally different things. Distinguishing
        them clarifies where optimization effort belongs.
      </p>
      <p>
        <strong>Raw Context</strong> — everything supplied by the user or the environment:
        conversation history, uploaded documents, voice input, images, prior session memory,
        tool outputs from external systems. This is the full signal available to the system.
        Richness here is desirable — more input bits help the system understand intent. This
        is where stream-of-consciousness interaction, long-form specification, and multimodal
        input all contribute value.
      </p>
      <p>
        <strong>Working Context</strong> — the planner&apos;s distilled understanding of the task:
        extracted entities, constraints, objectives, relevant relationships, and execution
        strategy. Working Context is a compression of Raw Context into structured, actionable
        form. This is where intent disambiguation happens — transforming a messy, rich input
        into a precise task representation.
      </p>
      <p>
        <strong>Execution Context</strong> — the minimum sufficient context delivered to a specific
        agent for a specific reasoning task. This is where Context Locality operates. Each
        agent receives only the subset of knowledge, instructions, and state it needs to execute
        its assigned task at full quality — nothing more.
      </p>
      <p>
        The optimization problem is different at each boundary:
      </p>
      <ul>
        <li>
          <strong>Raw → Working:</strong> Maximize signal extraction. The goal is to lose no
          relevant intent while compressing away noise, repetition, and structural irrelevance.
          More raw input is generally better.
        </li>
        <li>
          <strong>Working → Execution:</strong> Minimize token cost per agent while preserving
          reasoning quality. This is where the systems engineering challenge lives — and where
          the cost savings compound with agent count.
        </li>
      </ul>
      <p>
        This distinction resolves an apparent contradiction in the field: advice to &ldquo;give the
        model more context&rdquo; (correct at the Raw→Working boundary) coexists with evidence
        that excess context degrades reasoning (correct at the Working→Execution boundary).
        Both are true — they operate at different layers of the same pipeline.
      </p>

      <h2>Context Locality</h2>
      <p>
        We introduce <strong>Context Locality</strong> as the foundational principle of this
        discipline: <em>every reasoning task should operate on the smallest context sufficient to preserve reasoning quality.</em>
      </p>
      <p>
        This principle has several immediate implications:
      </p>
      <ul>
        <li>
          Context should not be partitioned by reasoning concern (security, correctness,
          performance) but by the natural structure of the domain.
        </li>
        <li>
          Agents operating on the same localized context should share that context rather
          than independently reconstructing it.
        </li>
        <li>
          The size of the context window consumed should be proportional to the blast radius
          of the triggering event — not to the size of the entire knowledge base.
        </li>
      </ul>
      <p>
        Context Locality is analogous to spatial locality in CPU caches: if a piece of
        information is relevant, nearby information (in the dependency graph) is likely
        relevant too. But distant information — however factually correct — dilutes reasoning
        quality by competing for attention within a finite context window.
      </p>

      <MathBlock
        label="Context Locality Objective"
        tex="C^* = \underset{C \subseteq G}{\operatorname{argmin}} \; |C| \quad \text{subject to} \quad Q\bigl(R(C)\bigr) \geq Q\bigl(R(G)\bigr)"
        where={[
          'where C ⊆ G is a subgraph of the domain graph G,',
          '|C| is the token cost of context C,',
          'R(C) is the reasoning output given context C,',
          'Q(·) is the quality function measuring reasoning effectiveness,',
          'and Q(R(G)) is the quality achievable with full-graph context.',
        ]}
      />

      <p>
        The optimization finds the smallest subgraph that preserves reasoning quality. This
        is a constrained optimization over graph structure — NP-hard in the general case,
        but tractable under the locality assumption that relevant information clusters
        spatially in the dependency graph.
      </p>

      <h2>Graph-Induced Context</h2>
      <p>
        Rather than treating knowledge bases as flat document stores, Context Engineering models
        them as graphs. Nodes represent entities (files, functions, documents, concepts) and
        edges represent relationships (imports, citations, dependencies, ownership).
      </p>
      <p>
        When an event occurs — a code change, a query, a new document — it activates a small
        region of this graph. The activated subgraph constitutes the natural unit of reasoning
        context.
      </p>
      <p>
        Consider a pull request that modifies three functions in a large repository. The
        relevant context is not the entire repository, nor even the entire files containing
        those functions. It is the dependency neighborhood: the functions that call the modified
        functions, the functions they call, the tests that exercise them, the interfaces they
        implement. This neighborhood is naturally defined by the dependency graph.
      </p>

      <div className="article-diagram">
        <img src={contextEngineeringGraph} alt="Context Locality — comparing traditional per-agent retrieval (O(N×|G|×k)) with locality-aware shared context construction (O(|B(e)| + N×p))" />
      </div>

      <p>
        This architecture separates two concerns that existing systems conflate:
        <strong> context construction</strong> (what to reason over) and
        <strong> reasoning specialization</strong> (how to reason). The graph determines the
        context; the agents determine the analysis. Multiple specialized agents can project
        different views from the same constructed context rather than independently searching
        the entire knowledge base.
      </p>

      <h2>The Planner as a First-Class Component</h2>
      <p>
        In this architecture, the planner is no longer merely a task router. It becomes the
        central intelligence responsible for:
      </p>
      <ol>
        <li>
          <strong>Graph activation:</strong> Given an event, determine which nodes and edges in
          the domain graph are relevant. This involves blast radius computation — how far from
          the triggering event should context extend?
        </li>
        <li>
          <strong>Context construction:</strong> Assemble the activated subgraph into a coherent
          context window, respecting token budgets and information density requirements.
        </li>
        <li>
          <strong>Reviewer projection:</strong> For each specialized agent, project the
          constructed context into a task-specific view. A security reviewer needs different
          emphasis than a performance reviewer, even when operating on the same code.
        </li>
        <li>
          <strong>Budget allocation:</strong> Distribute the available context window budget
          across components based on their relevance to the triggering event and the sensitivity
          of the downstream reasoning.
        </li>
      </ol>
      <p>
        This planner-centric architecture transforms the computational complexity of multi-agent
        systems. Instead of growing linearly with the number of agents (each independently
        retrieving context), cost grows with the size of the activated subgraph — typically
        much smaller than the full knowledge base and shared across all downstream reasoners.
      </p>

      <h2>Blast Radius</h2>
      <p>
        The concept of blast radius — borrowed from distributed systems failure analysis —
        provides the mechanism for determining context boundaries. Given a triggering event,
        the blast radius defines how far through the dependency graph its effects might
        propagate.
      </p>
      <p>
        A small, isolated change has a small blast radius: perhaps only the modified function
        and its direct callers need to be included in context. A change to a widely-used
        interface has a large blast radius: many dependents could be affected.
      </p>
      <p>
        Computing appropriate blast radius is itself a research problem. Too narrow, and the
        context misses relevant information — reasoning quality degrades. Too wide, and the
        context accumulates irrelevant information — the finite context window is wasted on
        noise, and again reasoning quality degrades.
      </p>
      <p>
        The optimal blast radius is the smallest subgraph that preserves reasoning quality.
        This is Context Locality in practice.
      </p>

      <h2>Generalization Beyond Software</h2>
      <p>
        Although motivated by code review, Context Engineering generalizes naturally to any
        domain with graph-structured knowledge:
      </p>
      <p>
        <strong>Legal reasoning:</strong> A new regulation activates a subgraph of affected
        contracts, precedents, and jurisdictional rules. The blast radius determines which
        contracts need review.
      </p>
      <p>
        <strong>Scientific discovery:</strong> A new experimental result activates a neighborhood
        of related hypotheses, prior experiments, and theoretical frameworks. The relevant context
        is the citation graph neighborhood, not the entire literature.
      </p>
      <p>
        <strong>Cybersecurity:</strong> A detected anomaly activates a subgraph of related
        network entities, access patterns, and historical incidents. The blast radius determines
        the scope of investigation.
      </p>
      <p>
        <strong>Medical diagnosis:</strong> A new symptom activates a neighborhood of related
        conditions, test results, patient history, and drug interactions. The dependency graph
        is the patient&apos;s medical knowledge graph.
      </p>
      <p>
        In each case, the same principle applies: reasoning quality improves when context is
        localized to the relevant subgraph rather than retrieved from the entire knowledge base.
      </p>

      <h2>Efficiency Implications</h2>
      <p>
        Context Locality has measurable efficiency implications across token cost,
        retrieval operations, reasoning quality, and latency:
      </p>
      <p>
        <strong>Token reduction:</strong> By constructing context from the activated subgraph
        rather than broadly retrieving from the full knowledge base, total token consumption
        decreases substantially. For a repository with 180K files, a typical pull request
        activates fewer than 200 nodes in the dependency graph — less than 2% of the
        total graph surface.
      </p>
      <p>
        <strong>Context reuse:</strong> When multiple agents share a constructed context (with
        task-specific projections), the retrieval and assembly cost is paid once rather than
        N times for N agents. For a system with 6 specialized reviewers, this represents
        an 83% reduction in retrieval operations.
      </p>
      <p>
        <strong>Reasoning quality:</strong> Smaller, more focused contexts reduce the attention
        dilution problem. Models reason more effectively when the context window contains
        predominantly relevant information rather than a mix of relevant and irrelevant material.
      </p>
      <p>
        <strong>Latency:</strong> Constructing context from a pre-indexed graph is faster than
        running N independent retrieval queries against a large corpus. The graph structure
        enables efficient traversal algorithms with bounded complexity.
      </p>

      <MathBlock
        label="Cost Complexity Comparison"
        tex="\underbrace{\mathcal{O}(N \times |G| \times k)}_{\text{Traditional}} \quad \longrightarrow \quad \underbrace{\mathcal{O}\bigl(|B(e)| + N \times p\bigr)}_{\text{Locality-Aware}}"
        where={[
          'where N = number of agents, |G| = graph size, k = retrieval depth,',
          '|B(e)| = blast radius of event e, p = projection cost per agent.',
          'Since |B(e)| ≪ |G| under locality, the reduction is significant.',
        ]}
      />

      <h2>Context Window Architecture</h2>
      <p>
        The context window is not a monolithic buffer — it is a structured architecture with
        distinct regions, each serving a different purpose and operating under different
        management policies:
      </p>
      <p>
        <strong>Instruction region:</strong> System prompts, behavioral constraints, and
        governance policies. This region is immutable during a session (modification constitutes
        instruction drift — a governance concern). It consumes a fixed token budget that must
        be subtracted from the available reasoning space before any dynamic content is loaded.
      </p>
      <p>
        <strong>Working context:</strong> The active information the agent is reasoning over —
        retrieved documents, tool outputs, intermediate results. This region is dynamic,
        managed by the context planner, and subject to the locality optimization described
        above. Its size fluctuates based on task complexity and blast radius.
      </p>
      <p>
        <strong>Conversation history:</strong> Prior interactions within the session. This
        region grows monotonically unless actively compressed. Left unmanaged, it becomes the
        dominant consumer of context budget in long-running sessions — a problem that
        session-native observability detects and context engineering addresses.
      </p>
      <p>
        <strong>Memory projection:</strong> Information retrieved from long-term memory stores —
        prior session summaries, user preferences, learned patterns. This region is curated by
        the memory lifecycle system (discussed below) and projected into the window at session
        initialization and at key decision points.
      </p>
      <p>
        Effective context engineering requires managing these regions as a unified budget.
        When total consumption approaches the window limit, the system must make principled
        decisions: compress history, evict low-relevance working context, or summarize and
        archive. These decisions are informed by locality analysis — information closer to
        the current reasoning task (in the dependency graph) is retained over distant
        information.
      </p>

      <h2>Context as a Versioned, Governed Artifact</h2>
      <p>
        In production systems, context is not ephemeral. It is constructed, validated, cached,
        and — critically — versioned. When a context construction strategy produces high-quality
        reasoning on Tuesday but degraded quality on Wednesday, you need to understand what
        changed. Was the input distribution different? Did the knowledge graph update? Did a
        retrieval policy change?
      </p>
      <p>
        Treating context as a versioned artifact enables several capabilities that ad-hoc
        context construction cannot provide:
      </p>
      <p>
        <strong>Reproducibility:</strong> Given the same event and the same knowledge graph
        state, the same context should be constructed. This enables debugging, regression
        testing, and A/B comparison of context strategies.
      </p>
      <p>
        <strong>Governance:</strong> Context that enters the window is subject to access
        control. An agent with security clearance level A should not receive context derived
        from documents at clearance level B. The context construction pipeline must enforce
        information boundaries — not rely on the model to ignore information it should not
        have seen.
      </p>
      <p>
        <strong>Auditability:</strong> For any reasoning output, you should be able to
        reconstruct exactly what context was present when that output was generated. This is
        the context-level equivalent of reproducible builds — given the inputs, you can
        verify the environment.
      </p>
      <p>
        <strong>Evolution tracking:</strong> How does context quality change as the knowledge
        graph grows, as retrieval strategies are refined, as compression algorithms improve?
        Versioned context enables longitudinal quality analysis.
      </p>

      <h2>Memory Lifecycle for Production Agents</h2>
      <p>
        Long-running and recurring agents maintain state across session boundaries through
        memory systems. Context Engineering provides the framework for managing memory as a
        lifecycle — not as a static store but as an evolving resource with distinct phases:
      </p>
      <p>
        <strong>Acquisition:</strong> During a session, the agent encounters information worth
        persisting — task outcomes, user preferences, environmental observations, learned
        heuristics. The acquisition decision is itself a context engineering problem: what
        information has value beyond the current session? What is the cost of storing it
        versus the cost of rediscovering it?
      </p>
      <p>
        <strong>Consolidation:</strong> Raw session memories are noisy. Consolidation
        compresses, deduplicates, and organizes acquired memories into retrievable units.
        This is analogous to database compaction — maintaining query performance as the
        store grows.
      </p>
      <p>
        <strong>Retrieval and projection:</strong> At session initialization and at key
        reasoning boundaries, relevant memories are retrieved and projected into the context
        window. The retrieval strategy is locality-aware — memories related to the current
        task (in the semantic graph) are prioritized over temporally recent but topically
        distant memories.
      </p>
      <p>
        <strong>Decay and eviction:</strong> Not all memories retain value indefinitely.
        Facts become stale. Heuristics become obsolete as the environment changes.
        Preferences drift. A production memory system must detect staleness and either
        refresh or evict memories whose continued presence would degrade reasoning quality.
      </p>
      <p>
        The connection to Context Locality is direct: memory retrieval is a context
        construction problem. The principles that govern which subgraph to include in
        working context also govern which memories to project into the session — minimum
        sufficient context, locality over completeness, quality over quantity.
      </p>

      <h2>Validation in Production Systems</h2>
      <p>
        The principles described above are not theoretical. We validate them across two
        production AI systems operating in distinct domains — one in runtime intelligence
        for AI agents, the other in multi-modal educational assessment.
      </p>

      <h3>Session-Native Observability (LexiLensAI)</h3>
      <p>
        LexiLensAI operates multiple analysis engines — cost estimation, anomaly detection,
        execution graph construction, and causal explanation generation — against the same
        underlying session data. Prior to adopting locality-aware context construction, each
        engine independently retrieved session events and assembled its own context window.
        For a session with 47 LLM calls spanning 340K tokens of raw telemetry, each engine
        was consuming 60-80K tokens of context to perform its analysis. With 4 engines
        operating independently, total context consumption per session analysis exceeded 280K
        tokens.
      </p>
      <p>
        Under graph-induced context construction, the system first builds the session&apos;s
        execution graph and identifies the activated subgraph — typically the causal
        neighborhood around detected anomalies. This shared constructed context (averaging
        35-50K tokens depending on session complexity) is then projected into engine-specific
        views: the cost estimator receives token flow data, the anomaly detector receives
        structural patterns, the explanation generator receives the causal chain. Total
        context consumption drops to approximately 90K tokens — a 68% reduction — while
        explanation quality scores (measured via LLM-as-judge rubrics) remain within 2% of
        the independent-retrieval baseline.
      </p>
      <p>
        The blast radius concept validates directly: an anomaly at event #22 in a 47-event
        session activates its causal neighborhood (typically 8-12 events spanning the
        triggering condition, the anomalous behavior, and its downstream effects). The
        remaining 35+ events are structurally irrelevant to explaining that specific anomaly.
        Including them in context would dilute the explanation engine&apos;s attention without
        improving analysis quality.
      </p>

      <h3>Multi-Modal Educational Intelligence (LexemeAI)</h3>
      <p>
        LexemeAI processes classroom observations across multiple modalities — audio
        transcription, video engagement signals, document artifacts, and handwritten notes —
        and generates longitudinal developmental insights for educators. The context
        engineering challenge here is observation retrieval: when generating a longitudinal
        pattern report, which prior observations constitute relevant context?
      </p>
      <p>
        The naive approach is full-corpus retrieval: embed the current observation, search
        the entire history for semantic similarity, return top-K results. For an educator
        with 200+ observations accumulated over multiple academic terms, this retrieves a
        mix of topically related but temporally scattered observations — some highly relevant,
        others connected only by surface-level keyword overlap.
      </p>
      <p>
        Under locality-aware retrieval, the system models observations as a graph with temporal
        edges (sequential observations), pattern edges (shared pedagogical themes), and
        development arc edges (belonging to the same growth trajectory). When a new observation
        arrives, the activated subgraph is its temporal neighborhood (recent observations from
        the same educator) intersected with its pattern cluster (observations sharing
        pedagogical dimensions). This typically activates 15-25 observations from the
        corpus — roughly 10-12% of the full history — while capturing the developmental
        trajectory that makes longitudinal insights meaningful.
      </p>
      <p>
        The measured effect: retrieval token cost reduced by approximately 75% (from full
        corpus embedding search to bounded graph traversal), while longitudinal coherence
        scores — measuring whether generated insights accurately reflect developmental
        trajectories over time — improved by 8-11%. The improvement is explained by Context
        Locality: smaller, more focused context allows the reasoning model to detect genuine
        patterns rather than being distracted by superficially similar but developmentally
        unrelated observations.
      </p>

      <h2>Connection to Observability</h2>
      <p>
        Context Engineering and runtime observability form a natural feedback loop. Session-native
        observability reveals how context accumulates within agent sessions — where it grows
        unbounded, where irrelevant information pollutes reasoning, where critical information
        is missing.
      </p>
      <p>
        These observations directly inform context construction strategies. If observability
        reveals that a certain class of agent sessions consistently experiences context window
        explosions after tool invocation #5, the context planner can preemptively compress or
        filter context at that boundary.
      </p>
      <p>
        This is the architectural connection between our session-native runtime intelligence
        work and our context engineering research: one observes the problem, the other solves it.
      </p>

      <h2>Open Research Questions</h2>
      <p>
        Context Engineering as a discipline raises several open problems that motivate our
        ongoing research program:
      </p>
      <p>
        <strong>Optimal blast radius computation:</strong> Given a triggering event and a token
        budget, what is the minimal subgraph that preserves reasoning quality? This is a
        constrained optimization problem over graph structure.
      </p>
      <p>
        <strong>Context compression:</strong> When the activated subgraph exceeds the available
        context window, how should information be compressed, summarized, or prioritized without
        losing critical reasoning inputs?
      </p>
      <p>
        <strong>Long-horizon context memory:</strong> For agents operating over extended time
        horizons, how should context be cached, evicted, and refreshed across session boundaries?
      </p>
      <p>
        <strong>Semantic context routing:</strong> When multiple knowledge sources are available,
        how should the planner route context requests to maximize information density within
        token budgets?
      </p>
      <p>
        <strong>Evaluation frameworks for context quality:</strong> How do we measure whether
        context construction is optimal? What metrics capture the relationship between context
        quality and reasoning quality?
      </p>

      <h2>Conclusion</h2>
      <p>
        The shift from optimizing task decomposition to optimizing context construction
        represents a fundamental reorientation in how we build multi-agent systems. Context
        Locality — the principle that reasoning should operate on the smallest sufficient
        context — provides both a theoretical foundation and a practical guide for this
        transition.
      </p>
      <p>
        As agent systems grow more complex, handle larger knowledge bases, and operate under
        tighter cost and latency constraints, the efficiency gains from principled context
        engineering will become not merely beneficial but necessary. We believe Context
        Engineering will emerge as an independent systems discipline — analogous to database
        systems, operating systems, and distributed systems — with its own theoretical
        foundations, algorithms, and engineering best practices.
      </p>

    </ArticleLayout>
  </div>
);

export default ContextEngineering;
